import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../App";
import functions from "../functions/functions";
import PopUp from "./PopUp";
import Allergens from "./Allergens";
import {
    autoSubstitute,
    isFilterActive,
    isRisky,
    reasonsLabel,
    riskReasons,
    swapInSingleChoice,
} from "../utils/allergenFilter";

function VariantsPopUp({ product }) {
    const { add_product, setPopup, apiSettings, allergenFilter, allergenFilterEnabled } = useContext(AppContext);
    const [p, setP] = useState(structuredClone(product));
    const [infoProduct, setInfoProduct] = useState(null);
    const [valid, setValid] = useState(false);
    const [total, setTotal] = useState(product.price);
    const [activeSection, setActiveSection] = useState(null);
    const [flashSection, setFlashSection] = useState(null);
    const sectionRefs = useRef({});
    const flashTimeoutRef = useRef(null);
    const variants2Enabled = Number(apiSettings?.menu_api?.enable_variants2) === 1;
    // filtro allergeni (YELLGO-6213): attivo solo se abilitato nelle impostazioni POS e con allergie scelte
    const filterOn = allergenFilterEnabled && isFilterActive(allergenFilter);
    const [allergenChanges, setAllergenChanges] = useState([]);

    useEffect(() => {
        setDefaults();
    }, []);

    useEffect(() => {
        validate();
        getTotal();
    }, [p]);

    useEffect(() => {
        const firstKey =
            !variants2Enabled && p.ingredients.length > 0
                ? "ingredients"
                : p.variants_categories.length > 0
                  ? "variant-0"
                  : p.ingredients.length > 0
                    ? "ingredients"
                    : null;
        if (!firstKey) {
            if (activeSection !== null) setActiveSection(null);
            return;
        }
        const currentIsValidVariant =
            typeof activeSection === "string" &&
            activeSection.startsWith("variant-") &&
            Number(activeSection.replace("variant-", "")) < p.variants_categories.length;
        const currentIsValidIngredients = activeSection === "ingredients" && p.ingredients.length > 0;
        if (!currentIsValidVariant && !currentIsValidIngredients) setActiveSection(firstKey);
    }, [p.variants_categories, p.ingredients, activeSection]);

    useEffect(() => {
        return () => {
            if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        };
    }, []);

    const setDefaults = () => {
        console.log("setDefaults");
        let newP = structuredClone(p);
        newP.variants_categories = [...(newP.variants_categories || [])].sort((a, b) => {
            const aPosition = Number(a?.position);
            const bPosition = Number(b?.position);
            const safeAPosition = Number.isFinite(aPosition) ? aPosition : Number.MAX_SAFE_INTEGER;
            const safeBPosition = Number.isFinite(bPosition) ? bPosition : Number.MAX_SAFE_INTEGER;
            if (safeAPosition !== safeBPosition) return safeAPosition - safeBPosition;
            return String(a?.name || "").localeCompare(String(b?.name || ""), "it");
        });
        newP.variants_categories.forEach((variants_category) => {
            if (variants_category.compressed === undefined || variants_category.compressed === null)
                variants_category.compressed = 0;
            variants_category.compressed = Number(variants_category.compressed) === 1 ? 1 : 0;
            ["variants", "variants2", "products", "products2"].forEach((group) => {
                if (!Array.isArray(variants_category[group])) variants_category[group] = [];
                variants_category[group] = [...variants_category[group]].sort((a, b) => {
                    const aPosition = Number(a?.position);
                    const bPosition = Number(b?.position);
                    const safeAPosition = Number.isFinite(aPosition) ? aPosition : Number.MAX_SAFE_INTEGER;
                    const safeBPosition = Number.isFinite(bPosition) ? bPosition : Number.MAX_SAFE_INTEGER;
                    if (safeAPosition !== safeBPosition) return safeAPosition - safeBPosition;
                    return String(a?.name || "").localeCompare(String(b?.name || ""), "it");
                });
                variants_category[group].forEach((item) => {
                    if (item.is_default > 0) item.qty = 1;
                });
            });
            variants_category.selected = countSelected(variants_category);
        });
        if (filterOn) {
            setAllergenChanges(
                autoSubstitute(newP, allergenFilter, {
                    manual: Number(apiSettings?.pos?.allergen_manual_substitute) === 1,
                    freeSubstitute: Number(apiSettings?.pos?.allergen_free_substitute) === 1,
                }),
            );
        }
        console.log(newP);
        setP(newP);
    };

    const increment = (type, i, n) => {
        console.log("increment");
        let newP = structuredClone(p);
        // categoria a scelta singola: il + su un'altra voce la scambia (solo con il filtro allergeni abilitato)
        if (allergenFilterEnabled && swapInSingleChoice(newP.variants_categories[i], type, n)) {
            setP(newP);
            return;
        }
        const qty = newP.variants_categories[i][type][n].qty ? newP.variants_categories[i][type][n].qty : 0;
        newP.variants_categories[i][type][n].qty = qty + 1;
        newP.variants_categories[i].selected = countSelected(newP.variants_categories[i]);
        setP(newP);
    };

    const decrement = (type, i, n) => {
        console.log("decrement");
        let newP = structuredClone(p);
        const qty = newP.variants_categories[i][type][n].qty ? newP.variants_categories[i][type][n].qty : 0;
        if (qty > 0) {
            newP.variants_categories[i][type][n].qty = qty - 1;
            newP.variants_categories[i].selected = countSelected(newP.variants_categories[i]);
            setP(newP);
        }
    };

    const countSelected = (vc) => {
        console.log("countSelected");
        let tot = 0;
        for (const v of vc.variants) {
            tot += v.qty ? v.qty : 0;
        }
        for (const v of vc.variants2) {
            tot += v.qty ? v.qty : 0;
        }
        for (const v of vc.products) {
            tot += v.qty ? v.qty : 0;
        }
        for (const v of vc.products2) {
            tot += v.qty ? v.qty : 0;
        }
        return tot;
    };

    const validate = () => {
        console.log("validate");
        let err = false;
        for (const vc of p.variants_categories) {
            if (!vc.selected) vc.selected = 0;
            const min = getMin(vc);
            console.log(vc.name, vc.selected, min, vc.selected < min);
            if (vc.selected < min) err = true;
        }
        console.log("err", err);
        setValid(!err);
    };

    const getTotal = () => {
        console.log("getTotal");
        let t = parseFloat(product.price);
        for (const vc of p.variants_categories) {
            for (const v of vc.variants) {
                if (v.qty) t += v.qty * v.price;
            }
            for (const v of vc.variants2) {
                if (v.qty) t += v.qty * v.price;
            }
            for (const v of vc.products) {
                if (v.qty) t += v.qty * v.price;
            }
            for (const v of vc.products2) {
                if (v.qty) t += v.qty * v.price;
            }
        }
        console.log("tot", t);
        setTotal(t);
    };

    const handleIngredients = (i) => {
        let newP = structuredClone(p);
        if (Number(newP.ingredients[i]?.removable) !== 1) return;
        const val = newP.ingredients[i].removed ? newP.ingredients[i].removed : false;
        newP.ingredients[i].removed = !val;
        setP(newP);
        console.log(newP.ingredients);
    };

    const toggleCompressed = (index) => {
        let newP = structuredClone(p);
        newP.variants_categories[index].compressed =
            Number(newP.variants_categories[index].compressed) === 1 ? 0 : 1;
        setP(newP);
    };

    const scrollToSection = (key) => {
        setActiveSection(key);
        setFlashSection(key);
        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => setFlashSection(null), 700);
        const target = sectionRefs.current[key];
        if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const allergenFilterLabel = () =>
        reasonsLabel([
            ...allergenFilter.allergens.map((id) => ({ type: "allergen", id: Number(id) })),
        ]) +
        (allergenFilter.diets.length
            ? (allergenFilter.allergens.length ? " · " : "") +
              allergenFilter.diets
                  .map((d) => ({ vegan: "Vegano", gluten_free: "Senza glutine", lactose_free: "Senza lattosio" })[d])
                  .join(", ")
            : "");

    const getMin = (vc) => {
        const min = Number(vc?.min);
        return Number.isFinite(min) ? min : 0;
    };

    const getMaxValue = (vc) => {
        const max = Number(vc?.max);
        return Number.isFinite(max) ? max : Number.MAX_SAFE_INTEGER;
    };

    const getMaxLabel = (vc) => {
        const max = Number(vc?.max);
        return Number.isFinite(max) ? max : "∞";
    };

    const isAllergenLocked = (v, type) => filterOn && (type == "products" || type == "products2") && isRisky(v, allergenFilter);

    const canSwap = (vc, type, n) => {
        if (!allergenFilterEnabled) return false;
        return swapInSingleChoice(JSON.parse(JSON.stringify(vc)), type, n);
    };

    const canIncrement = (vc, v, type, n) => {
        if (isAllergenLocked(v, type)) return false;
        if (canSwap(vc, type, n)) return true;
        const selected = Number(vc?.selected || 0);
        const forceMono = Number(vc?.force_mono || 0) === 1;
        const max = getMaxValue(vc);
        if (type == "variants" || type == "variants2") {
            return !selected || (!forceMono && selected < max) || (forceMono && (!v.qty || v.qty < 1));
        }
        return !selected || selected < max;
    };

    const getVariantItems = (vc) => {
        const groups = ["variants", "variants2", "products", "products2"];
        const items = [];
        groups.forEach((group) => {
            (vc[group] || []).forEach((item, n) => {
                items.push({ type: group, n, item });
            });
        });
        return items;
    };

    const isIngredientRemovable = (ingredient) => Number(ingredient?.removable) === 1;

    const isIngredientsVariantCategory = (vc) =>
        Number(vc?.ingredients) === 1 || Number(vc?.is_ingredients) === 1 || Number(vc?.ingredienti) === 1;

    const shouldHideVariantSelectors = (vc, item) =>
        isIngredientsVariantCategory(vc) && Number(item?.removable) === 0;

    const splitVariantItems = (vc) => {
        const items = getVariantItems(vc);
        const middle = Math.ceil(items.length / 2);
        return [items.slice(0, middle), items.slice(middle)];
    };

    const renderVariantItem = (vc, i, descriptor) => {
        const { type, n, item: v } = descriptor;
        const qty = v.qty ? v.qty : 0;
        const incrementEnabled = canIncrement(vc, v, type, n);
        const allergenLocked = isAllergenLocked(v, type);
        const isAutoSubstitute = filterOn && Number(v.allergen_substitute) === 1 && qty > 0;
        const isAllergenRemoved = filterOn && Number(v.allergen_removed) === 1 && qty === 0;
        const hideSelectors = shouldHideVariantSelectors(vc, v);
        const isSubproduct = type == "products" || type == "products2";
        return (
            <div
                key={type + "-" + n + "-" + v.id}
                className={"variant" + (allergenLocked ? " allergen-locked" : "") + (isAutoSubstitute ? " allergen-auto" : "")}
            >
                <div className="variant-main">
                    {isSubproduct ? (
                        <div className="variant-name subproduct-name">
                            <span>{v.name}</span>
                            {v.allergens?.length > 0 && <Allergens allergens={v.allergens} iconsOnly />}
                            {isAutoSubstitute && <span className="allergen-tag auto">AUTO</span>}
                            {isAllergenRemoved && <span className="allergen-tag removed">TOLTO PER ALLERGIA</span>}
                            {allergenLocked && !isAllergenRemoved && (
                                <span className="allergen-tag locked">
                                    {"🔒 " + reasonsLabel(riskReasons(v, allergenFilter)).toUpperCase()}
                                </span>
                            )}
                            <button
                                type="button"
                                className="subproduct-info-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setInfoProduct(v);
                                }}
                            >
                                i
                            </button>
                        </div>
                    ) : (
                        <div className="variant-name">{v.name}</div>
                    )}
                    {Number(vc?.has_price) == 1 && (
                        <div className="variant-price">{functions.formatter.format(v.price)}</div>
                    )}
                </div>
                {hideSelectors ? (
                    <div className="plus-minus" aria-hidden="true">
                        <div>{qty > 0 ? qty : ""}</div>
                    </div>
                ) : (
                    <div className="plus-minus">
                        <button
                            onClick={() => decrement(type, i, n)}
                            className={!qty || qty == 0 ? "disabled" : ""}
                            disabled={!qty || qty == 0}
                        >
                            -
                        </button>
                        <div>{qty}</div>
                        <button
                            onClick={() => incrementEnabled && increment(type, i, n)}
                            className={incrementEnabled ? "" : "disabled"}
                            disabled={!incrementEnabled}
                        >
                            +
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="inner-content">
                <div className="variants-popup">
                    {filterOn && (
                        <div className="allergen-substitutions">
                            <div className="bold">{"Filtro allergie: " + allergenFilterLabel()}</div>
                            {allergenChanges.map((c, k) => (
                                <div key={k} className={"allergen-change " + c.type}>
                                    {c.type === "swap" && (
                                        <>
                                            <span className="from">{c.from.name}</span>
                                            {" → "}
                                            <span className="to">{c.to.name}</span>
                                        </>
                                    )}
                                    {c.type === "manual" && (
                                        <>
                                            <span className="from">{c.from.name}</span>
                                            {" tolto: scegli un'alternativa in " + c.vcName}
                                        </>
                                    )}
                                    {c.type === "remove" && (
                                        <>
                                            <span className="from">{c.from.name}</span>
                                            {" tolto per allergia"}
                                        </>
                                    )}
                                    {c.type === "unresolved" && (
                                        <>
                                            <span className="from">{c.from.name}</span>
                                            {" non ha alternative compatibili in " + c.vcName}
                                        </>
                                    )}
                                </div>
                            ))}
                            <div className="small">
                                Le voci con il lucchetto non si possono scegliere. Per forzarle spegni il filtro.
                            </div>
                        </div>
                    )}
                    <div className="variants-categories-nav">
                        {!variants2Enabled && p.ingredients.length > 0 && (
                            <button
                                type="button"
                                className={activeSection === "ingredients" ? "selected" : ""}
                                onClick={() => scrollToSection("ingredients")}
                            >
                                Rimuovi ingredienti
                            </button>
                        )}
                        {p.variants_categories.map((vc, i) => (
                            <button
                                type="button"
                                key={i}
                                className={activeSection === "variant-" + i ? "selected" : ""}
                                onClick={() => scrollToSection("variant-" + i)}
                            >
                                {vc.name}
                            </button>
                        ))}
                        {variants2Enabled && p.ingredients.length > 0 && (
                            <button
                                type="button"
                                className={activeSection === "ingredients" ? "selected" : ""}
                                onClick={() => scrollToSection("ingredients")}
                            >
                                Rimuovi ingredienti
                            </button>
                        )}
                    </div>
                    <div
                        className={
                            "variants-category ingredients-section" +
                            (flashSection === "ingredients" ? " section-flash" : "")
                        }
                        ref={(el) => (sectionRefs.current.ingredients = el)}
                        aria-live="polite"
                    >
                        <div className="variants-categories-title">
                            <div>
                                <div className="bold">Rimuovi ingredienti</div>
                            </div>
                        </div>
                        <div className="list-variant-ingredient">
                            {p.ingredients.map((ingredient, i) => (
                                <label
                                    key={i}
                                    htmlFor={"ingredient-" + i}
                                    className={
                                        "variant ingredient-variant" +
                                        (isIngredientRemovable(ingredient) ? "" : " disabled")
                                    }
                                    aria-disabled={!isIngredientRemovable(ingredient)}
                                >
                                    <input
                                        id={"ingredient-" + i}
                                        type="checkbox"
                                        checked={ingredient.removed ? false : true}
                                        disabled={!isIngredientRemovable(ingredient)}
                                        onChange={() => handleIngredients(i)}
                                    />
                                    <span>{ingredient.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    {p.variants_categories.map((vc, i) => (
                        <div
                            key={i}
                            ref={(el) => (sectionRefs.current["variant-" + i] = el)}
                            className={
                                "variants-category" +
                                (Number(vc.selected || 0) < getMin(vc) ? " error" : "") +
                                (flashSection === "variant-" + i ? " section-flash" : "")
                            }
                        >
                            <div
                                className="variants-categories-title"
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleCompressed(i)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleCompressed(i);
                                    }
                                }}
                            >
                                <div>
                                    <div className="bold">{vc.name}</div>
                                    <div className="small">
                                        {"Min: " +
                                            getMin(vc) +
                                            " - Max: " +
                                            (Number(vc.force_mono) == 0 ? getMaxLabel(vc) : 1)}
                                    </div>
                                </div>
                                <div className="variants-title-actions">
                                    {Number(vc.selected || 0) < getMin(vc) && (
                                        <div className="request-tag">Richiesto</div>
                                    )}
                                    <div className="variants-selected-counter">{vc.selected || 0}</div>
                                    <button
                                        type="button"
                                        className="variants-compress-toggle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleCompressed(i);
                                        }}
                                    >
                                        {vc.compressed != 1 ? "Comprimi" : "Espandi"}
                                    </button>
                                </div>
                            </div>
                            <div
                                className={
                                    "variants-category-body" + (vc.compressed == 1 ? " compressed" : "")
                                }
                            >
                                {(() => {
                                    const [leftCol, rightCol] = splitVariantItems(vc);
                                    return (
                                        <>
                                            <div className="variant-column">
                                                {leftCol.map((descriptor) =>
                                                    renderVariantItem(vc, i, descriptor),
                                                )}
                                            </div>
                                            <div className="variant-column">
                                                {rightCol.map((descriptor) =>
                                                    renderVariantItem(vc, i, descriptor),
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="variants-buttons">
                <div className="variants-total-box">
                    <div className="variants-total-value">{functions.formatter.format(total)}</div>
                </div>
                <div className="variants-actions">
                    <button
                        className={"variants-button" + (!valid ? " error" : "")}
                        disabled={!valid}
                        onClick={() => {
                            if (!valid) return;
                            add_product(p, total, true);
                            setPopup(false);
                        }}
                    >
                        {valid ? "Aggiungi" : "Effettua tutte le scelte"}
                    </button>
                </div>
            </div>
            {infoProduct && (
                <PopUp
                    size="sm"
                    customClass="subproduct-info-popup"
                    options={{
                        title: infoProduct.name,
                        onClose: () => setInfoProduct(null),
                        content: (
                            <div className="subproduct-info">
                                {infoProduct.img && (
                                    <div className="subproduct-info-img">
                                        <img src={infoProduct.img} alt={infoProduct.name} />
                                    </div>
                                )}
                                {infoProduct.description && (
                                    <div className="subproduct-info-section">
                                        <div className="bold">Descrizione</div>
                                        <div
                                            className="subproduct-info-description"
                                            dangerouslySetInnerHTML={{ __html: infoProduct.description }}
                                        />
                                    </div>
                                )}
                                {infoProduct.allergens?.length > 0 && (
                                    <div className="subproduct-info-section">
                                        <div className="bold">Allergeni</div>
                                        <Allergens allergens={infoProduct.allergens} />
                                    </div>
                                )}
                            </div>
                        ),
                    }}
                />
            )}
        </>
    );
}

export default VariantsPopUp;
